import { escapeHtml } from "../chrome";
import { apiFetch } from "../api";
import { formatDate, formatRelative } from "./format";
import type { ReportStats, DailyHit } from "./types";

export interface OpenStatsPanelArgs {
  clientSlug: string;
  reportSlug: string;
  uploadId: string;
  title: string;
}

export async function openStatsPanel({
  clientSlug,
  reportSlug,
  uploadId,
  title,
}: OpenStatsPanelArgs): Promise<void> {
  const overlay = document.createElement("div");
  overlay.className =
    "fixed inset-0 z-40 bg-black/60 backdrop-blur-sm animate-[fadeIn_120ms_ease-out]";
  document.body.appendChild(overlay);

  const panel = document.createElement("aside");
  panel.className =
    "fixed top-0 right-0 bottom-0 w-full max-w-2xl bg-ink border-l border-ink-border z-50 overflow-y-auto " +
    "animate-[slideInRight_180ms_ease-out]";
  panel.setAttribute("role", "dialog");
  panel.setAttribute("aria-modal", "true");
  panel.innerHTML = renderShell(title, "Loading stats…", true);
  document.body.appendChild(panel);
  document.body.style.overflow = "hidden";

  const close = (): void => {
    document.body.style.overflow = "";
    panel.remove();
    overlay.remove();
    document.removeEventListener("keydown", onKey);
  };
  const onKey = (e: KeyboardEvent): void => {
    if (e.key === "Escape") close();
  };
  document.addEventListener("keydown", onKey);
  overlay.addEventListener("click", close);
  panel.addEventListener("click", (e) => {
    const target = e.target as HTMLElement;
    if (target?.dataset.action === "close") close();
  });

  try {
    const stats = await apiFetch<ReportStats>(
      `/api/reports/${clientSlug}/${reportSlug}/${uploadId}/stats`,
    );
    panel.innerHTML = renderShell(title, renderBody(stats, clientSlug, reportSlug), false);
  } catch (err) {
    console.error(err);
    panel.innerHTML = renderShell(title, renderError(err), false);
  }
}

function renderShell(title: string, body: string, loading: boolean): string {
  return `
    <header class="sticky top-0 bg-ink-surface/95 backdrop-blur border-b border-ink-border px-6 py-4 flex items-center justify-between">
      <div class="min-w-0 pr-4">
        <div class="text-xs uppercase tracking-wider text-bone-muted">Report stats</div>
        <h2 class="text-base font-semibold tracking-tight truncate">${escapeHtml(title)}</h2>
      </div>
      <button data-action="close" class="text-bone-muted hover:text-bone p-2 -mr-2 rounded" aria-label="Close">
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
          <path d="M1 1L13 13M13 1L1 13" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
        </svg>
      </button>
    </header>
    <div class="p-6 ${loading ? "text-bone-muted text-sm" : ""}">${body}</div>
  `;
}

function renderError(err: unknown): string {
  return `
    <p class="text-red-300 text-sm">Failed to load stats.</p>
    <pre class="mt-3 text-xs text-bone-muted font-mono whitespace-pre-wrap">${escapeHtml(String(err))}</pre>
  `;
}

function renderBody(stats: ReportStats, clientSlug: string, reportSlug: string): string {
  const hasHits = stats.totalHits > 0;
  return `
    <div class="grid grid-cols-3 gap-4 mb-8">
      ${stat(
        "Client views",
        String(stats.totalHits),
        stats.totalInternalHits > 0 ? `+ ${stats.totalInternalHits} internal (TFG)` : undefined,
      )}
      ${stat("First opened", stats.firstHitAt ? formatDate(stats.firstHitAt) : "Not yet")}
      ${stat("Last opened", stats.lastHitAt ? formatRelative(stats.lastHitAt) : "Not yet")}
    </div>

    <div class="mb-2 flex items-baseline justify-between">
      <h3 class="text-xs uppercase tracking-wider text-bone-muted">Views over time</h3>
      ${hasHits ? `<span class="text-xs text-bone-muted font-mono">${stats.dailyHits.length} day${stats.dailyHits.length === 1 ? "" : "s"} with hits</span>` : ""}
    </div>
    <div class="card p-4 mb-8">
      ${hasHits ? renderSparkline(stats.dailyHits) : renderEmptyChart()}
    </div>

    <div class="mb-2 flex items-baseline justify-between">
      <h3 class="text-xs uppercase tracking-wider text-bone-muted">Where it's been opened</h3>
      ${stats.locations.length > 0 ? `<span class="text-xs text-bone-muted font-mono">${stats.locations.length} location${stats.locations.length === 1 ? "" : "s"}</span>` : ""}
    </div>
    <div class="card">
      ${stats.locations.length > 0 ? renderLocations(stats) : renderEmptyLocations()}
    </div>

    <p class="text-xs text-bone-muted mt-8">
      <span class="font-mono">${escapeHtml(clientSlug)}/${escapeHtml(reportSlug)}</span> ·
      Uploaded ${escapeHtml(formatDate(stats.uploadedAt))} ·
      Expires ${escapeHtml(formatDate(stats.expiresAt))}
    </p>
    <p class="text-xs text-bone-muted/70 mt-2">
      Stats refresh once a day at 01:00 UTC. CloudFront edge POPs identify the rough viewer
      country; not every POP is mapped, so unknown ones show as raw airport codes.
    </p>
  `;
}

function stat(label: string, value: string, note?: string): string {
  return `
    <div class="card px-4 py-3">
      <div class="text-xs uppercase tracking-wider text-bone-muted">${escapeHtml(label)}</div>
      <div class="text-xl font-semibold mt-1 tabular-nums">${escapeHtml(value)}</div>
      ${note ? `<div class="text-xs text-bone-muted mt-0.5">${escapeHtml(note)}</div>` : ""}
    </div>
  `;
}

function renderEmptyChart(): string {
  return `<p class="text-sm text-bone-muted text-center py-6">No views yet. Stats appear here once the link has been opened.</p>`;
}

function renderEmptyLocations(): string {
  return `<p class="text-sm text-bone-muted text-center py-6 px-4">No views yet.</p>`;
}

function renderLocations(stats: ReportStats): string {
  const total = stats.locations.reduce((sum, l) => sum + l.hits, 0);
  return `
    <ul>
      ${stats.locations
        .map((l) => {
          const pct = total > 0 ? Math.round((l.hits / total) * 100) : 0;
          return `
          <li class="flex items-center gap-3 px-4 py-2.5 border-b border-ink-border last:border-0">
            <span class="flex-1 text-sm">${escapeHtml(l.label)}</span>
            <span class="text-xs text-bone-muted font-mono tabular-nums w-10 text-right">${pct}%</span>
            <span class="text-sm font-semibold tabular-nums w-10 text-right">${l.hits}</span>
          </li>
        `;
        })
        .join("")}
    </ul>
  `;
}

// ── Sparkline: a vanilla SVG bar chart, no chart library ──────────────────
//
// Renders the last 30 days inclusive of the most recent hit. Days with no
// hits show as a faint background bar so the time axis stays continuous and
// gaps in engagement are obvious. Day with the max gets the brand-blue fill.

function renderSparkline(daily: DailyHit[]): string {
  const padded = padToLastNDays(daily, 30);
  const max = Math.max(1, ...padded.map((d) => d.hits));
  const width = 100;
  const height = 32;
  const barWidth = width / padded.length;

  const bars = padded
    .map((d, i) => {
      const h = d.hits === 0 ? 1.5 : Math.max(1.5, (d.hits / max) * height);
      const x = i * barWidth;
      const y = height - h;
      const fill = d.hits === 0 ? "#23252C" : "#006EEE";
      const titleSafe = `${d.date}: ${d.hits} view${d.hits === 1 ? "" : "s"}`;
      return `<rect x="${x.toFixed(3)}" y="${y.toFixed(3)}" width="${(barWidth - 0.4).toFixed(3)}"
      height="${h.toFixed(3)}" fill="${fill}" rx="0.5">
      <title>${escapeHtml(titleSafe)}</title>
    </rect>`;
    })
    .join("");

  const firstLabel = padded[0]?.date ?? "";
  const lastLabel = padded[padded.length - 1]?.date ?? "";

  return `
    <svg viewBox="0 0 ${width} ${height}" preserveAspectRatio="none"
         class="w-full h-24" role="img"
         aria-label="Daily hits over the last ${padded.length} days">
      ${bars}
    </svg>
    <div class="flex justify-between mt-2 text-xs text-bone-muted font-mono">
      <span>${escapeHtml(formatDate(firstLabel + "T00:00:00Z"))}</span>
      <span>Peak: <span class="text-bone">${max}</span> / day</span>
      <span>${escapeHtml(formatDate(lastLabel + "T00:00:00Z"))}</span>
    </div>
  `;
}

function padToLastNDays(daily: DailyHit[], n: number): DailyHit[] {
  if (daily.length === 0) {
    const today = new Date();
    return Array.from({ length: n }, (_, i) => {
      const d = new Date(today);
      d.setUTCDate(today.getUTCDate() - (n - 1 - i));
      return { date: d.toISOString().slice(0, 10), hits: 0, internalHits: 0 };
    });
  }

  const sorted = [...daily].sort((a, b) => a.date.localeCompare(b.date));
  const last = sorted[sorted.length - 1]!.date;
  const lastDate = new Date(last + "T00:00:00Z");
  const map = new Map(sorted.map((d) => [d.date, d]));

  const result: DailyHit[] = [];
  for (let i = n - 1; i >= 0; i--) {
    const d = new Date(lastDate);
    d.setUTCDate(lastDate.getUTCDate() - i);
    const iso = d.toISOString().slice(0, 10);
    const day = map.get(iso);
    // The chart plots client views only; internal views ride along so a
    // future overlay doesn't need this function changed again.
    result.push({ date: iso, hits: day?.hits ?? 0, internalHits: day?.internalHits ?? 0 });
  }
  return result;
}
